import {
  Avatar,
  Box,
  Checkbox,
  FormControlLabel,
  Tooltip,
} from "@mui/material";
import Button from "@mui/material/Button";
import Dialog from "components/NoScrollDialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import ColorPickerInput from "components/ColorPickerInput";
import DevelopmentFeature from "components/DevelopmentFeature";
import DropdownSelect from "components/DropdownSelect";
import LoadingButton from "components/LoadingButton";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MSelect from "components/MSelect";
import { useAuth } from "contexts/auth.context";
import { useLanguage } from "contexts/language.context";
import * as React from "react";
import toast from "react-hot-toast";
import accountsService from "services/accounts-service";
import useObjectState from "utils/useObjectState";
import featuresService from "services/features-service";
import departmentsService from "services/departments-service";
import userGroupsService from "services/user-groups-service";
import EmailInput from "#components/EmailsInput/index.js";

export default function UserDialog({
  isOpen,
  action,
  data,
  onSubmit,
  accountId,
  groupedRoles = true,
}) {
  const { label } = useLanguage();
  const auth = useAuth();
  const [open, setOpen] = React.useState({ dialog: false });
  const [loading, setLoading] = React.useState(false);
  const [value, setValue] = React.useState({
    name: "",
    email: "",
    roleId: 0,
    color: "#eeeeee",
  });
  const [updated, setUpdated] = React.useState({});
  const [error, setError] = React.useState(null);
  const [options, changeOptions] = useObjectState({
    accounts: null,
    features: null,
  });

  React.useEffect(() => {
    if (!isOpen) return;
    setOpen({ dialog: true });
    setLoading(false);
    setError(null);
  }, [isOpen]);

  React.useEffect(() => {
    if (action == "create") {
      setValue({
        name: "",
        email: "",
        roleId: data?.roles[0]?.id,
        color: "#eeeeee",
      });
    } else {
      setValue(data?.user);
    }
  }, [data]);

  const accessLevelTypes = React.useMemo(() => {
    const value = [{ id: 1, label: "Account (Everything)" }];

    if (data.accounts?.length) {
      value.push({ id: 2, label: "Sub-account" });
    }

    if (data.branchGroups?.length) {
      value.push({ id: 3, label: "Branch group" });
    }

    if (data.branchGroups?.length) {
      value.push({ id: 4, label: "Branch" });
    }
    return value;
  }, [data]);

  const accessLevel = React.useMemo(() => {
    let type = 1;
    let id = null;

    if (value.accessLevel?.accountId) {
      type = 2;
      id = value?.accessLevel.accountId;
    }

    if (value.accessLevel?.branchGroupId) {
      type = 3;
      id = value?.accessLevel.branchGroupId;
    }

    if (value.accessLevel?.branchId) {
      type = 4;
      id = value?.accessLevel.branchId;
    }

    return { type, value: id };
  }, [value]);

  const handleChangeAccessLevel = (type, id) => {
    const accessLevel = {};
    if (type === 2) {
      accessLevel.accountId = id;
    } else if (type === 3) {
      accessLevel.branchGroupId = id;
    } else if (type === 4) {
      accessLevel.branchId = id;
    }

    setValue({ ...value, accessLevel });
  };

  const handleGetOptions = async () => {
    const [accountsResponse, featuresResponse, departmentsResponse] =
      await Promise.all([
        accountsService.getAccounts({ raw: true }),
        featuresService.getFeatures(),
        departmentsService.getDepartments(),
      ]);

    let [response, data] = accountsResponse;
    if (response.ok) {
      changeOptions("accounts", data);
    } else {
    }

    [response, data] = featuresResponse;
    if (response.ok) {
      changeOptions("features", data);
    } else {
    }

    [response, data] = departmentsResponse;
    if (response.ok) {
      changeOptions("departments", data);
    } else {
    }
  };

  const getUserGroups = async () => {
    const [response, newData] = await userGroupsService.getUserGroups({
      accountId: value.accountId || data.accountId,
    });
    if (response.ok) {
      changeOptions("userGroups", newData);
    } else {
    }
  };

  React.useEffect(() => {
    handleGetOptions();
  }, []);

  React.useEffect(() => {
    if (!value.accountId && !data.accountId) return;
    getUserGroups();
  }, [value.accountId, data.accountId]);

  const handleClose = () => {
    setOpen(false);
  };

  const dropdownOptions = React.useMemo(() => {
    return {
      accounts:
        options.accounts?.map((account) => ({
          id: account.id,
          label: account.name,
        })) || [],
      features:
        options.features?.map((feature) => ({
          id: feature.id,
          label: feature.name,
        })) || [],
      departments:
        options.departments?.map((department) => ({
          id: department.id,
          label: department.description,
        })) || [],
      roles:
        data?.roles
          ?.sort((a, b) => a.account?.id - b.account?.id)
          ?.map((role) => {
            return {
              id: role.id,
              label: role.description,
              account: role?.account?.name || "No account",
            };
          }) || [],
      userGroups: options.userGroups?.map((userGroup) => ({
        id: userGroup.id,
        label: userGroup.name,
      })),
    };
  }, [options, data]);

  const hasUserGroups = React.useMemo(() => {
    return (
      value.account?.hasUserGroups ||
      data.account?.hasUserGroups ||
      options.accounts?.find((account) => account.id == value.accountId)
        ?.hasUserGroups
    );
  }, [
    value.account?.hasUserGroups,
    data.account?.hasUserGroups,
    options.accounts,
    value.accountId,
  ]);

  return (
    <Dialog
      fullWidth
      maxWidth="sm"
      open={open.dialog}
      onClose={handleClose}
      PaperProps={{
        component: "form",
        onSubmit: async (event) => {
          event.preventDefault();

          setLoading(true);
          const result = await onSubmit(value);
          if (result?.success) {
            handleClose();
          } else {
            setError(result.error);

            if (!result.error.fields) toast.error(`${result.error.message}`);
          }

          setLoading(false);
        },
      }}
    >
      <DialogTitle sx={{ m: 0, p: 2, pb: 0.4, pt: 1.4 }}>
        {action == "edit" ? label("edit-user") : label("create-user")}
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={2} mt={0.5}>
          <Grid item xs={12}>
            <TextField
              autoFocus
              required
              error={error?.fields === "name"}
              helperText={error?.fields === "name" && error?.message}
              id="r"
              name="r"
              label={label("name-singular")}
              type="text"
              value={value.name}
              onChange={(e) => {
                setValue({ ...value, name: e.target.value });
              }}
              fullWidth
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              required
              id="r"
              name="r"
              label={label("email-singular")}
              type="email"
              error={error?.fields === "email"}
              helperText={error?.fields === "email" && error?.message}
              value={value.email}
              onChange={(e) => {
                setValue({ ...value, email: e.target.value });
              }}
              fullWidth
            />
          </Grid>
          <Grid item xs={12}>
            <DropdownSelect
              label={label("role-singular")}
              error={error?.fields === "roleId"}
              helperText={error?.fields === "roleId" && error?.message}
              items={dropdownOptions.roles}
              initialValue={value.roleId || ""}
              onChange={(e) => {
                setValue({ ...value, roleId: e?.id });
              }}
              required
              groupBy={groupedRoles ? (option) => option.account : false}
              fullWidth
            />
          </Grid>

          <Grid item xs={12}>
            <DropdownSelect
              label={label("feature-plural")}
              error={error?.fields === "featureId"}
              helperText={error?.fields === "featureId" && error?.message}
              items={dropdownOptions.features}
              initialValue={value.features?.map((v) => v.id) || []}
              onChange={(features) => {
                setValue({ ...value, features });
              }}
              fullWidth
              multiple
            />
          </Grid>

          {hasUserGroups ? (
            <Grid item xs={6}>
              <Tooltip
                title={
                  !value.accountId && !data?.accountId
                    ? label("select-account-first")
                    : null
                }
              >
                <Box>
                  <DropdownSelect
                    label={label("user-group-singular")}
                    error={error?.fields === "userGroupId"}
                    helperText={
                      error?.fields === "userGroupId" && error?.message
                    }
                    items={dropdownOptions.userGroups || []}
                    initialValue={value.userGroupId || ""}
                    onChange={(e) => {
                      setValue({ ...value, userGroupId: e?.id });
                    }}
                    fullWidth
                    disabled={!value.accountId && !data?.accountId}
                  />
                </Box>
              </Tooltip>
            </Grid>
          ) : null}
          <Grid item xs={hasUserGroups ? 6 : 12}>
            <DropdownSelect
              label={label("department-singular")}
              error={error?.fields === "departmentId"}
              helperText={error?.fields === "departmentId" && error?.message}
              items={dropdownOptions.departments}
              initialValue={value.departmentId || ""}
              onChange={(d) => {
                setValue({ ...value, departmentId: d?.id });
              }}
              fullWidth
            />
          </Grid>

          <Grid
            item
            xs={12}
            sx={{ mt: 0.5 }}
            onClick={() => {
              setOpen({ ...open, color: !open.color });
            }}
          >
            <ColorPickerInput
              required
              id="color"
              name="color"
              label={label("color-singular")}
              type="color"
              value={value.color}
              onChange={(e) => {
                setValue({ ...value, color: e.target.value });
              }}
              setValue={(v) => {
                setValue({ ...value, color: v });
              }}
              fullWidth
            />
          </Grid>

          {!accountId && (
            <Grid item xs={12}>
              <DropdownSelect
                required
                label={"Account"}
                initialValue={value?.accountId}
                onChange={(e) => {
                  setValue({ ...value, accountId: e?.id, userGroupId: null });
                }}
                items={dropdownOptions.accounts}
                fullWidth
                renderOption={(option) => (
                  <MDBox
                    sx={{
                      display: "flex",
                      flexDirection: "row",
                      gap: 1,
                      alignItems: "center",
                    }}
                  >
                    <Avatar
                      sx={{ height: "30px", width: "30px" }}
                      src={option.url}
                    />
                    <MDTypography variant="body" sx={{ fontWeight: "400" }}>
                      {option.label}
                    </MDTypography>
                  </MDBox>
                )}
              />
            </Grid>
          )}

          {auth.isTechnicalAdministrator ? (
            <Grid item xs={12}>
              <FormControlLabel
                label={label("show-audits-for-my-accounts-only")}
                checked={!!value.showAuditsForMyAccounts}
                onChange={(e) => {
                  setValue({
                    ...value,
                    showAuditsForMyAccounts: e.target.checked,
                  });
                }}
                control={<Checkbox />}
              />
            </Grid>
          ) : null}

          <Grid item xs={12}>
            <EmailInput
              label="Notified emails (press Enter to add)"
              value={value.notifiedEmails || ""}
              onChange={(newValue) => {
                setValue((v) => {
                  let notifiedEmails;
                  if (typeof newValue == "function")
                    notifiedEmails = newValue(
                      v.notifiedEmails
                        ?.split(",")
                        .map((v) => v.trim())
                        .filter(Boolean) || [],
                    ).join(",");
                  else notifiedEmails = newValue.join(",");
                  return { ...v, notifiedEmails };
                });
              }}
            />
          </Grid>

          {/*<Grid item xs={!accessLevel.type || accessLevel.type === 1 ? 12 : 6}>
            <DropdownSelect
              label="Access level type"
              required
              initialValue={accessLevel.type || ""}
              onChange={(newValue) => {
                handleChangeAccessLevel(newValue?.id, -1);
              }}
              items={accessLevelTypes}
              fullWidth
            />
          </Grid>

          {accessLevel.type === 2 && (
            <Grid item xs={6}>
              <DropdownSelect
                label="Select"
                required
                initialValue={accessLevel.value || ""}
                onChange={(newValue) => {
                  handleChangeAccessLevel(2, newValue?.id);
                }}
                items={
                  data.accounts?.map((account) => {
                    return {
                      id: account.id,
                      label: account.name,
                    };
                  }) || []
                }
                fullWidth
              />
            </Grid>
          )}

          {accessLevel.type === 3 && (
            <Grid item xs={6}>
              <DropdownSelect
                label="Select"
                required
                initialValue={accessLevel.value || ""}
                onChange={(newValue) => {
                  handleChangeAccessLevel(3, newValue?.id);
                }}
                items={
                  data.branchGroups?.map((branchGroup) => {
                    return {
                      id: branchGroup.id,
                      label: branchGroup.name,
                    };
                  }) || []
                }
                fullWidth
              />
            </Grid>
          )}

          {accessLevel.type === 4 && (
            <Grid item xs={6}>
              <DropdownSelect
                label="Select"
                required
                initialValue={accessLevel.value || ""}
                onChange={(newValue) => {
                  handleChangeAccessLevel(4, newValue?.id);
                }}
                items={
                  data.branches?.map((branch) => {
                    return {
                      id: branch.id,
                      label: branch.name,
                      branchGroup: branch.branchGroup?.name,
                    };
                  }) || []
                }
                groupBy={(option) => option.branchGroup}
                fullWidth
              />
            </Grid>
          )}*/}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          {label("cancel-button")}
        </Button>
        <LoadingButton
          loading={loading}
          type="submit"
          variant="gradient"
          color="info"
        >
          {action == "edit" ? label("save-button") : label("create-button")}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}
